import { RSAA } from 'redux-api-middleware';
import { Dispatch } from 'redux';
import {
  TGetRootState,
  TAsyncDispatchResult,
  TDispatchResult,
} from 'types/shared';

const GET_REQUEST = 'OPUS/SELECTED_CUSTOM_DSP_LIST/GET_REQUEST';
const PUT_REQUEST = 'OPUS/SELECTED_CUSTOM_DSP_LIST/PUT_REQUEST';
const GET_SUCCESS = 'OPUS/SELECTED_CUSTOM_DSP_LIST/GET_SUCCESS';
const PUT_SUCCESS = 'OPUS/SELECTED_CUSTOM_DSP_LIST/PUT_SUCCESS';
const FAILURE = 'OPUS/SELECTED_CUSTOM_DSP_LIST/FAILURE';

type SelectedCustomDSPListActionTypes =
  | typeof GET_REQUEST
  | typeof PUT_REQUEST
  | typeof GET_SUCCESS
  | typeof PUT_SUCCESS
  | typeof FAILURE;

interface ISelectedCustomDSPListAction {
  type: SelectedCustomDSPListActionTypes;
  error?: boolean;
  payload: any;
}

export interface ISelectedCustomDSPListState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  reason: any;
  value: any;
}

const initialState: ISelectedCustomDSPListState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  reason: null,
  value: null,
};

export default function reducer(
  state: ISelectedCustomDSPListState = initialState,
  action: ISelectedCustomDSPListAction
): ISelectedCustomDSPListState {
  switch (action.type) {
    case GET_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
      };
    case PUT_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
        value: action.payload,
      };
    case PUT_SUCCESS:
      // We do not update the on put success because we are updating optimistically
      // and the user might already have newer settings once the success comes in.
      // If the PUT call fails the user will see an error message in the DSP selector
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
      };
    case GET_SUCCESS:
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
        value: action.payload,
      };
    case FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: Boolean(action.error),
        reason: action.payload,
      };

    default:
      return state;
  }
}

export function saveSelectedCustomDSPList(
  customDSPList: any,
  key?: string
): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const { selectedCustomDSPList } = getState();
    const current = selectedCustomDSPList.value;

    const payload = key ? { ...current, [key]: customDSPList } : customDSPList;

    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: PUT_REQUEST,
            payload: payload,
          },
          {
            type: PUT_SUCCESS,
            payload: payload,
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_selected_custom_dsp_list`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      },
    });
  };
}

/**
 * This action creators load the users' currently selected customDSPList, or
 * it creates a new record in the key/value store.
 */
export function loadSelectedCustomDSPList(): TAsyncDispatchResult {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    // get the user id
    const { id } = getState().authentication.profile;
    const { selectedCustomDSPList } = getState();

    // fetch the users' selected customDSPList
    const actionResponse: any = await dispatch({
      [RSAA]: {
        bailout:
          selectedCustomDSPList.pending || selectedCustomDSPList.fulfilled,
        types: [
          GET_REQUEST,
          {
            type: GET_SUCCESS,
            payload: async function payload(
              action: ISelectedCustomDSPListAction,
              state: ISelectedCustomDSPListState,
              res: any
            ): Promise<any> {
              const selectedCustomDSPList = await res.json();

              return selectedCustomDSPList;
            },
          },
          {
            type: FAILURE,
            meta: (_action, _state, res: any): void => {
              if (res?.status === 404) {
                saveSelectedCustomDSPList(null, 'streaming')(
                  dispatch,
                  getState
                );
              }
            },
          },
        ],
        endpoint: `/kv/user_${id}_selected_custom_dsp_list`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
    return actionResponse;
  };
}

export const shouldLoadSelectedCustomDSPList = (): TDispatchResult<boolean> => {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const state = getState();
    const { selectedCustomDSPList } = state;
    const { pending, value } = selectedCustomDSPList;
    return !value && !pending;
  };
};
